import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import {
    FaCalendarAlt, FaClock, FaVideo, FaRegCalendarCheck,
    FaHistory, FaTimes, FaUserAlt, FaMapMarkerAlt,
    FaGraduationCap, FaCheck, FaVideoSlash, FaArrowRight,
    FaUserCircle, FaBriefcase, FaEnvelope, FaPhone,
    FaLinkedin, FaGithub, FaFileAlt, FaCheckCircle
} from 'react-icons/fa';
import Loader from '../../shared/Loader';
import { showSuccess, showError, showConfirm } from '../../../utils/Alert';
import { API_URL } from '../../../config';
import './InterviewCenter.css';

const InterviewCenter = () => {
    const [interviews, setInterviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('upcoming');
    const [shortlistedApplicants, setShortlistedApplicants] = useState([]);

    // Candidate detail modal (for history tab)
    const [selectedHistoryCandidate, setSelectedHistoryCandidate] = useState(null);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [fullCandidateData, setFullCandidateData] = useState(null);

    const initialFormState = {
        jobId: '',
        candidateId: '',
        applicationId: '',
        scheduledDate: '',
        timeSlot: '',
        meetLink: '',
        notes: ''
    };

    const [formData, setFormData] = useState(initialFormState);

    useEffect(() => {
        fetchInterviews();
        fetchCandidates();
    }, []);

    const fetchInterviews = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/recruiter/interviews', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setInterviews(res.data);
        } catch (err) {
            console.error("Error fetching interviews:", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCandidates = async () => {
        try {
            const token = localStorage.getItem('token');
            const jobsRes = await axios.get('/api/recruiter/jobs', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const activeJobs = jobsRes.data.filter(j => j.status === 'Active');

            let allCandidates = [];
            for (const job of activeJobs) {
                const appRes = await axios.get(`/api/recruiter/jobs/${job._id}/applicants`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const eligible = appRes.data.filter(a => a.status === 'Shortlisted');
                allCandidates = [...allCandidates, ...eligible.map(e => ({ ...e, jobTitle: job.title }))];
            }
            setShortlistedApplicants(allCandidates);
        } catch (err) {
            console.error("Error fetching candidates:", err);
        }
    };

    const handleSchedule = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        try {
            const app = shortlistedApplicants.find(a => a.studentId._id === formData.candidateId && a.jobId === formData.jobId);
            const payload = { ...formData, applicationId: app?._id };

            await axios.post('/api/recruiter/interviews', payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIsModalOpen(false);
            fetchInterviews();
            fetchCandidates();
            showSuccess("Interview Scheduled", "The invitation has been sent to the candidate.");
        } catch (err) {
            showError("Schedule Failed", "Could not coordinate interview at this time.");
        }
    };

    const handleCompleteInterview = async (interviewId) => {
        const confirm = await showConfirm(
            'Mark as Completed',
            'Are you sure you want to mark this interview as completed? This will update the candidate\'s status and move the interview to History.'
        );
        if (!confirm.isConfirmed) return;

        setActionLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.patch(`/api/recruiter/interviews/${interviewId}/complete`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            showSuccess('Interview Completed', 'The interview has been marked as completed and moved to history.');
            await fetchInterviews();
        } catch (err) {
            showError('Action Failed', 'Could not mark the interview as completed.');
        } finally {
            setActionLoading(false);
        }
    };

    const openHistoryModal = async (interview) => {
        setSelectedHistoryCandidate(interview);
        setFullCandidateData(null); // reset
        setIsHistoryModalOpen(true);

        // Fetch fresh full profile data for the candidate
        try {
            const token = localStorage.getItem('token');
            // Get full student profile via existing recruiter job applicants route
            const jobId = interview.jobId?._id;
            if (jobId) {
                const res = await axios.get(`/api/recruiter/jobs/${jobId}/applicants`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const match = res.data.find(a =>
                    a.studentId?._id === (interview.candidateId?._id || interview.candidateId)
                );
                if (match) setFullCandidateData(match.studentId);
            }
        } catch (err) {
            console.error('Could not fetch full candidate profile:', err);
        }
    };

    // ---- Column Definitions ----
    const upcomingColumns = [
        {
            header: 'Candidate',
            render: (row) => (
                <div className="candidate-name-cell">
                    <strong>{row.candidateId?.name}</strong>
                    <span>{row.jobId?.title}</span>
                </div>
            )
        },
        {
            header: 'Scheduled Date',
            render: (row) => (
                <div className="date-cell">
                    <FaCalendarAlt /> {new Date(row.scheduledDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
            )
        },
        {
            header: 'Time',
            render: (row) => (
                <div className="time-cell">
                    <FaClock /> {row.timeSlot}
                </div>
            )
        },
        {
            header: 'Meet Link',
            render: (row) => (
                <div className="link-cell">
                    {row.meetLink ? (
                        <a href={row.meetLink} target="_blank" rel="noreferrer" className="meet-btn">
                            <FaVideo /> Join
                        </a>
                    ) : (
                        <span className="no-link"><FaVideoSlash /> Not set</span>
                    )}
                </div>
            )
        },
        {
            header: 'Status',
            render: (row) => (
                <span className={`status-badge ${row.status.toLowerCase()}`}>
                    {row.status}
                </span>
            )
        },
        {
            header: 'Actions',
            render: (row) => (
                <div className="ic-action-btns">
                    <button
                        className="ic-complete-btn"
                        onClick={() => handleCompleteInterview(row._id)}
                        disabled={actionLoading}
                        title="Mark as Completed"
                    >
                        <FaCheckCircle /> Complete
                    </button>
                </div>
            )
        }
    ];

    const historyColumns = [
        {
            header: 'Candidate',
            render: (row) => (
                <div className="candidate-name-cell">
                    <strong>{row.candidateId?.name}</strong>
                    <span>{row.jobId?.title}</span>
                </div>
            )
        },
        {
            header: 'Date',
            render: (row) => (
                <div className="date-cell">
                    <FaCalendarAlt /> {new Date(row.scheduledDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
            )
        },
        {
            header: 'Time',
            render: (row) => (
                <div className="time-cell">
                    <FaClock /> {row.timeSlot}
                </div>
            )
        },
        {
            header: 'Status',
            render: (row) => (
                <span className={`status-badge ${row.status.toLowerCase()}`}>
                    {row.status}
                </span>
            )
        },
        {
            header: 'Actions',
            render: (row) => (
                <div className="ic-action-btns">
                    <button
                        className="ic-view-btn"
                        onClick={() => openHistoryModal(row)}
                        title="View Candidate Details"
                    >
                        <FaUserCircle /> View Candidate
                    </button>
                </div>
            )
        }
    ];

    if (loading) return <Loader />;

    const upcoming = interviews.filter(i => i.status === 'Scheduled' || i.status === 'Rescheduled');
    const history = interviews.filter(i => i.status === 'Completed' || i.status === 'Cancelled');

    const renderTable = (columns, data, emptyMsg) => (
        <div className="ic-table-wrapper">
            <table className="ic-table">
                <thead>
                    <tr>
                        {columns.map((col, i) => <th key={i}>{col.header}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {data.length > 0 ? data.map((row) => (
                        <tr key={row._id}>
                            {columns.map((col, i) => (
                                <td key={i}>{col.render(row)}</td>
                            ))}
                        </tr>
                    )) : (
                        <tr>
                            <td colSpan={columns.length} className="ic-empty-row">
                                <FaRegCalendarCheck size={36} />
                                <p>{emptyMsg}</p>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="dashboard-overview fade-in">
            <div className="dashboard-header">
                <h1 className="page-title">Interview Center</h1>
                <p className="page-subtitle">Coordinate rounds and connect with top-tier candidates.</p>
            </div>

            <div className="tabs-container">
                <button
                    className={`tab-btn ${activeTab === 'upcoming' ? 'active' : ''}`}
                    onClick={() => setActiveTab('upcoming')}
                >
                    <FaCalendarAlt /> Upcoming Schedules
                    {upcoming.length > 0 && <span className="tab-count">{upcoming.length}</span>}
                </button>
                <button
                    className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                    onClick={() => setActiveTab('history')}
                >
                    <FaHistory /> Interview History
                    {history.length > 0 && <span className="tab-count">{history.length}</span>}
                </button>
            </div>

            {activeTab === 'upcoming' ? (
                <div className="premium-card fade-in">
                    <div className="ic-card-header">
                        <h3>Planned Conversations</h3>
                    </div>
                    {renderTable(upcomingColumns, upcoming, 'No upcoming interviews scheduled.')}
                </div>
            ) : (
                <div className="premium-card fade-in">
                    <div className="ic-card-header">
                        <h3>Completed &amp; Archived Discussions</h3>
                    </div>
                    {renderTable(historyColumns, history, 'No interview history yet. Complete an interview to see it here.')}
                </div>
            )}

            {/* Schedule Modal */}
            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content premium-card schedule-modal">
                        <div className="modal-header">
                            <h2>Schedule Technical Round</h2>
                            <button className="modal-close-btn" onClick={() => setIsModalOpen(false)}><FaTimes /></button>
                        </div>

                        <form onSubmit={handleSchedule} className="schedule-form">
                            <div className="form-group">
                                <label>Selection Context (Shortlisted Candidates)</label>
                                <select
                                    value={formData.candidateId}
                                    onChange={(e) => {
                                        const cand = shortlistedApplicants.find(a => a.studentId._id === e.target.value);
                                        setFormData({ ...formData, candidateId: e.target.value, jobId: cand?.jobId || '' });
                                    }}
                                    required
                                >
                                    <option value="">Select a Candidate</option>
                                    {shortlistedApplicants.map(app => (
                                        <option key={app._id} value={app.studentId._id}>
                                            {app.studentId.name} ({app.jobTitle})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Interview Date</label>
                                    <input
                                        type="date"
                                        value={formData.scheduledDate}
                                        onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Time Slot</label>
                                    <input
                                        type="text"
                                        value={formData.timeSlot}
                                        onChange={(e) => setFormData({ ...formData, timeSlot: e.target.value })}
                                        placeholder="e.g. 11:30 AM - 12:30 PM"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Meeting Link (Google Meet/Zoom)</label>
                                <div className="input-with-icon">
                                    <FaVideo className="input-icon" />
                                    <input
                                        type="url"
                                        value={formData.meetLink}
                                        onChange={(e) => setFormData({ ...formData, meetLink: e.target.value })}
                                        placeholder="https://meet.google.com/..."
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Preparation Notes (Optional)</label>
                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    placeholder="Add any specific requirements or context for the candidate..."
                                    rows="3"
                                ></textarea>
                            </div>

                            <div className="modal-footer">
                                <p className="helper-msg">Student will be notified immediately via dashboard and email.</p>
                                <div className="footer-actions">
                                    <button type="button" className="secondary-btn" onClick={() => setIsModalOpen(false)}>Cancel</button>
                                    <button type="submit" className="primary-btn">Confirm Schedule <FaArrowRight /></button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* History Candidate Detail Modal */}
            {isHistoryModalOpen && selectedHistoryCandidate && ReactDOM.createPortal(
                <div className="jl-modal-overlay open" onClick={() => setIsHistoryModalOpen(false)}>
                    <div className="jl-modal-content ts-profile-modal" onClick={e => e.stopPropagation()}>

                        {/* Header */}
                        <div className="ts-profile-hero">
                            <div className="ts-hero-left">
                                <div className="ts-large-avatar">
                                    {selectedHistoryCandidate.candidateId?.profileImage ? (
                                        <img src={`${API_URL}${selectedHistoryCandidate.candidateId.profileImage}`} alt="Profile" />
                                    ) : (
                                        <FaUserCircle size={72} />
                                    )}
                                </div>
                                <div className="ts-hero-text">
                                    <h2>{selectedHistoryCandidate.candidateId?.name || 'Candidate'}</h2>
                                    <p className="ts-hero-tagline">
                                        {selectedHistoryCandidate.candidateId?.academicInfo?.education || selectedHistoryCandidate.jobId?.title || 'Interview Candidate'}
                                    </p>
                                    <div className="ts-hero-tags">
                                        {selectedHistoryCandidate.candidateId?.location && (
                                            <span className="ts-tag"><FaMapMarkerAlt /> {selectedHistoryCandidate.candidateId.location}</span>
                                        )}
                                        <span className="ts-tag"><FaBriefcase /> Applied for {selectedHistoryCandidate.jobId?.title}</span>
                                    </div>
                                    <div className="ts-social-row">
                                        {selectedHistoryCandidate.candidateId?.academicInfo?.linkedin && (
                                            <a href={selectedHistoryCandidate.candidateId.academicInfo.linkedin} target="_blank" rel="noreferrer" className="ts-social-btn ts-li"><FaLinkedin /> LinkedIn</a>
                                        )}
                                        {selectedHistoryCandidate.candidateId?.academicInfo?.github && (
                                            <a href={selectedHistoryCandidate.candidateId.academicInfo.github} target="_blank" rel="noreferrer" className="ts-social-btn ts-gh"><FaGithub /> GitHub</a>
                                        )}
                                        {selectedHistoryCandidate.candidateId?.resumeUrl && (
                                            <a href={`${API_URL}${selectedHistoryCandidate.candidateId.resumeUrl}`} target="_blank" rel="noreferrer" className="ts-social-btn ts-resume"><FaFileAlt /> View Resume</a>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="ts-status-box">
                                <span className={`status-badge ${selectedHistoryCandidate.status.toLowerCase()}`}>
                                    {selectedHistoryCandidate.status}
                                </span>
                                {selectedHistoryCandidate.applicationId?.appliedAt && (
                                    <p className="ts-applied-date">Applied {new Date(selectedHistoryCandidate.applicationId.appliedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                                )}
                            </div>
                        </div>

                        {/* Body */}
                        <div className="jl-modal-body ts-profile-body">
                            <div className="ts-info-grid">

                                {/* Contact */}
                                <div className="ts-info-card">
                                    <h3><FaEnvelope /> Contact Information</h3>
                                    <div className="ts-info-rows">
                                        <div className="ts-info-row"><span>Email</span><p>{fullCandidateData?.email || selectedHistoryCandidate.candidateId?.email || '—'}</p></div>
                                        <div className="ts-info-row">
                                            <span>Phone</span>
                                            <p style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                {(fullCandidateData?.phone || selectedHistoryCandidate.candidateId?.phone)
                                                    ? <><FaPhone style={{ color: '#1D4ED8', fontSize: '12px' }} /> {fullCandidateData?.phone || selectedHistoryCandidate.candidateId?.phone}</> :
                                                    'Not provided'}
                                            </p>
                                        </div>
                                        <div className="ts-info-row"><span>Location</span><p>{fullCandidateData?.location || selectedHistoryCandidate.candidateId?.location || 'Not specified'}</p></div>
                                    </div>
                                </div>

                                {/* Academic & Career */}
                                <div className="ts-info-card">
                                    <h3><FaGraduationCap /> Academic &amp; Career</h3>
                                    <div className="ts-info-rows">
                                        <div className="ts-info-row"><span>Education</span><p>{fullCandidateData?.academicInfo?.education || selectedHistoryCandidate.candidateId?.academicInfo?.education || 'Not specified'}</p></div>
                                        <div className="ts-info-row"><span>Bio</span><p>{fullCandidateData?.bio || selectedHistoryCandidate.candidateId?.bio || 'No bio provided'}</p></div>
                                        <div className="ts-info-row"><span>Expected Salary</span><p>{fullCandidateData?.careerPreferences?.expectedSalary || selectedHistoryCandidate.candidateId?.careerPreferences?.expectedSalary || 'Open to discussion'}</p></div>
                                    </div>
                                </div>

                                {/* Skills */}
                                <div className="ts-info-card ts-full-width">
                                    <h3><FaBriefcase /> Skills &amp; Expertise</h3>
                                    <div className="ts-skills-cloud">
                                        {(fullCandidateData?.academicInfo?.skills || selectedHistoryCandidate.candidateId?.academicInfo?.skills)?.length > 0 ? (
                                            (fullCandidateData?.academicInfo?.skills || selectedHistoryCandidate.candidateId?.academicInfo?.skills || []).map((skill, i) => (
                                                <span key={i} className="ts-skill-tag">{skill}</span>
                                            ))
                                        ) : (
                                            <p className="ts-empty-text">No skills listed by candidate</p>
                                        )}
                                    </div>
                                </div>

                                {/* Preferred Locations */}
                                {(fullCandidateData?.careerPreferences?.preferredLocations || selectedHistoryCandidate.candidateId?.careerPreferences?.preferredLocations)?.length > 0 && (
                                    <div className="ts-info-card ts-full-width">
                                        <h3><FaMapMarkerAlt /> Preferred Work Locations</h3>
                                        <div className="ts-skills-cloud">
                                            {(fullCandidateData?.careerPreferences?.preferredLocations || selectedHistoryCandidate.candidateId?.careerPreferences?.preferredLocations || []).map((loc, i) => (
                                                <span key={i} className="ts-skill-tag ts-loc-tag">{loc}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Interview Details */}
                                <div className="ts-info-card ts-full-width">
                                    <h3><FaCalendarAlt /> Interview Details</h3>
                                    <div className="ts-info-rows">
                                        <div className="ts-info-row"><span>Date</span><p>{new Date(selectedHistoryCandidate.scheduledDate).toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</p></div>
                                        <div className="ts-info-row"><span>Time</span><p>{selectedHistoryCandidate.timeSlot}</p></div>
                                        {selectedHistoryCandidate.notes && <div className="ts-info-row"><span>Prep Notes</span><p>{selectedHistoryCandidate.notes}</p></div>}
                                        {selectedHistoryCandidate.meetLink && (
                                            <div className="ts-info-row">
                                                <span>Meet Link</span>
                                                <p><a href={selectedHistoryCandidate.meetLink} target="_blank" rel="noreferrer" style={{ color: '#1D4ED8' }}>Open Meeting Link</a></p>
                                            </div>
                                        )}
                                        {selectedHistoryCandidate.applicationId?.feedback && (
                                            <div className="ts-info-row"><span>Feedback</span><p>{selectedHistoryCandidate.applicationId.feedback}</p></div>
                                        )}
                                    </div>
                                </div>

                                {/* Cover Letter */}
                                {selectedHistoryCandidate.applicationId?.coverLetter && (
                                    <div className="ts-info-card ts-full-width">
                                        <h3><FaFileAlt /> Cover Letter</h3>
                                        <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.8', whiteSpace: 'pre-wrap', margin: 0 }}>
                                            {selectedHistoryCandidate.applicationId.coverLetter}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="jl-modal-actions ts-profile-footer">
                            <button className="jl-btn-secondary" onClick={() => setIsHistoryModalOpen(false)}>Close</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default InterviewCenter;
